import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import useViewport from "../../hooks/useViewport";

/**
 * Portrait hint for tablets - replaces the old full-screen RotateDevicePopup.
 *
 * A web page cannot lock orientation on iPad Safari, so the old overlay could
 * only ever be a wall: it had no dismiss, was mounted by hand on ~11 pages
 * (including Login and the public customer booking page), and missed the iPad
 * mini (744) and iPad Pro 13" (1032) entirely. This one is mounted once, in
 * Layout, never blocks, remembers a dismissal for the session, and only shows
 * on the dense screens that genuinely read better in landscape. Login,
 * ProfileSetup and the public booking page sit outside Layout and never show it.
 */
const DENSE_ROUTES = [
  /^\/calendar(\/|$)/,
  /^\/vendor_bookings(\/|$)/,
  /^\/dashboard\/\d/, // DashboardDetailed: /dashboard/:date
  /^\/expense-management\/branch\//,
  /^\/hr\/roster(\/|$)/,
];

const STORAGE_KEY = "rotateHintDismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const RotateHint = () => {
  const { pathname } = useLocation();
  const { isTablet, isPortrait, isCoarse } = useViewport();
  const [dismissed, setDismissed] = useState(readDismissed);

  const dense = DENSE_ROUTES.some((re) => re.test(pathname));
  if (dismissed || !dense || !isTablet || !isPortrait || !isCoarse) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* private mode - the hint just returns on the next load */
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        left: "50%",
        // Sits above the WhatsApp call-sound prompt, which owns the bottom 14px.
        bottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
        transform: "translateX(-50%)",
        zIndex: 1040,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        maxWidth: "calc(100vw - 32px)",
        padding: "6px 6px 6px 16px",
        background: "#1f2937",
        color: "#fff",
        borderRadius: "999px",
        fontSize: "13px",
        fontWeight: 500,
        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
      }}
    >
      <span>This screen works best in landscape.</span>
      <button
        type="button"
        onClick={dismiss}
        style={{
          minHeight: "44px",
          minWidth: "44px",
          padding: "0 16px",
          border: "none",
          borderRadius: "999px",
          background: "rgba(255,255,255,0.14)",
          color: "#fff",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Got it
      </button>
    </div>
  );
};

export default RotateHint;
